import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import { OFFLINE_SKIP_CACHE_FALLBACK, OFFLINE_SYNC_REPLAY } from '@shared/constants/offline.constants';
import { OfflineEndpointKey } from '@shared/constants/offline-endpoint-policy.constants';
import { TOAST_TYPE } from '@shared/constants/toast.constants';
import { NetworkStatusService } from '@shared/services/offline/network-status.service';
import { OfflineFormStateService } from '@shared/services/offline/offline-form-state.service';
import { OfflinePolicyService } from '@shared/services/offline/offline-policy.service';
import { OfflineQueueService } from '@shared/services/offline/offline-queue.service';
import { OfflineStorageService } from '@shared/services/offline/offline-storage.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { buildOfflineCacheKey } from '@shared/utilities/offline-cache-key.util';
import { shouldAttemptNetworkCacheFallback } from '@shared/utilities/offline-fallback.util';
import { catchError, from, of, switchMap, tap, throwError } from 'rxjs';

const TECHNICIAN_SEARCH_ENDPOINTS: OfflineEndpointKey[] = [
  'TECHNICIAN_FORMS_SEARCH',
  'TECHNICIAN_SCHEDULE',
  'TECHNICIAN_SCHEDULE_EVENTS',
  'TECHNICIAN_COMPETENCIES_SEARCH',
  'TECHNICIAN_UPDATE_LOGS_SEARCH',
];

const WORKSPACE_DEFAULT_KEY_FIELDS: Partial<Record<OfflineEndpointKey, keyof NonNullable<
  import('@shared/models/offline/offline.model').OfflineTechnicianWorkspace['defaultCacheKeys']
>>> = {
  TECHNICIAN_FORMS_SEARCH: 'formsSearch',
  TECHNICIAN_SCHEDULE: 'assignedRequestsSearch',
  TECHNICIAN_SCHEDULE_EVENTS: 'scheduleEvents',
  TECHNICIAN_COMPETENCIES_SEARCH: 'competenciesSearch',
  TECHNICIAN_UPDATE_LOGS_SEARCH: 'updateLogsSearch',
};

export const offlineInterceptor: HttpInterceptorFn = (req, next) => {
  const policy = inject(OfflinePolicyService);
  const networkStatus = inject(NetworkStatusService);
  const storage = inject(OfflineStorageService);
  const formState = inject(OfflineFormStateService);
  const queue = inject(OfflineQueueService);
  const toastService = inject(ToastService);

  if (!policy.isOfflineModeEnabled() || req.context.get(OFFLINE_SYNC_REPLAY)) {
    return next(req);
  }

  const endpoint = policy.resolveEndpointPolicy(req.url);
  if (!endpoint || endpoint.module !== 'service' || !policy.isModuleEnabled('service')) {
    return next(req);
  }

  const isOnline = networkStatus.isOnline();
  const bodyParams = (req.body ?? {}) as Record<string, unknown>;
  const clientId = resolveClientId(req, bodyParams);
  const skipCacheFallback = req.context.get(OFFLINE_SKIP_CACHE_FALLBACK);

  if (endpoint.policy === 'cache-read' && req.method === 'GET') {
    const params = Object.fromEntries(req.params.keys().map((key) => [key, req.params.get(key)]));
    const cacheKey = buildOfflineCacheKey(clientId, endpoint.key, params);
    const serveParams = {
      storage,
      toastService,
      req,
      cacheKey,
      clientId,
      endpointKey: endpoint.key,
      technicianId: Number(params['technicianId'] ?? 0),
    };

    if (!isOnline) {
      return serveCachedOrFallback(serveParams);
    }

    return handleOnlineCacheRead(
      req,
      next,
      serveParams,
      skipCacheFallback,
      async (event) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }

        const guarded = await isGuardedFormCacheWrite(endpoint.key, params, formState, clientId);
        if (guarded) {
          return;
        }

        await storage.putHttpCache({
          cacheKey,
          endpointKey: endpoint.key,
          url: req.url,
          method: req.method,
          body: event.body,
          status: event.status,
          cachedAt: new Date().toISOString(),
        });
      },
    );
  }

  if (endpoint.policy === 'cache-read' && req.method === 'POST') {
    const cacheKey = buildOfflineCacheKey(clientId, endpoint.key, bodyParams);
    const serveParams = {
      storage,
      toastService,
      req,
      cacheKey,
      clientId,
      endpointKey: endpoint.key,
      technicianId: Number(bodyParams['technicianId'] ?? 0),
    };

    if (!isOnline) {
      return serveCachedOrFallback(serveParams);
    }

    return handleOnlineCacheRead(
      req,
      next,
      serveParams,
      skipCacheFallback,
      async (event) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }

        const guarded = await isGuardedFormCacheWrite(endpoint.key, bodyParams, formState, clientId);
        if (guarded) {
          return;
        }

        await storage.putHttpCache({
          cacheKey,
          endpointKey: endpoint.key,
          url: req.url,
          method: req.method,
          body: event.body,
          status: event.status,
          cachedAt: new Date().toISOString(),
        });
      },
    );
  }

  if (endpoint.policy === 'queue-write' && !isOnline) {
    const serviceRequestId = Number(bodyParams['serviceRequestId'] ?? 0);
    const formId = Number(bodyParams['formId'] ?? 0);
    const formCode = String(bodyParams['formCode'] ?? '').trim();

    return from(
      endpoint.key === 'ADD_FORM_RESPONSE'
        ? queue.enqueueAddFormResponse(bodyParams as never, serviceRequestId, formCode, formId)
        : queue.enqueueUpdateFormResponse(bodyParams as never, serviceRequestId, formCode, formId),
    ).pipe(
      switchMap((mutation) => of(new HttpResponse({
        body: {
          success: true,
          clientMutationId: mutation.clientMutationId,
          responseId: 'responseId' in bodyParams ? bodyParams['responseId'] : undefined,
        },
        status: 200,
      }))),
      catchError((error) => throwError(() => error)),
    );
  }

  return next(req);
};

function handleOnlineCacheRead(
  req: HttpRequest<unknown>,
  next: Parameters<HttpInterceptorFn>[1],
  serveParams: ServeCachedParams,
  skipCacheFallback: boolean,
  onSuccess: (event: unknown) => Promise<void>,
) {
  return next(req).pipe(
    tap((event) => {
      void onSuccess(event);
    }),
    catchError((error) => {
      if (skipCacheFallback || !shouldAttemptNetworkCacheFallback(error)) {
        return throwError(() => error);
      }
      return serveCachedOrFallback(serveParams);
    }),
  );
}

function resolveClientId(req: HttpRequest<unknown>, bodyParams: Record<string, unknown>): string {
  if (bodyParams['clientId']) {
    return String(bodyParams['clientId']);
  }

  const paramClientId = req.params.get('clientId');
  if (paramClientId) {
    return paramClientId;
  }

  return environment.CLIENT_ID;
}

interface ServeCachedParams {
  storage: OfflineStorageService;
  toastService: ToastService;
  req: HttpRequest<unknown>;
  cacheKey: string;
  clientId: string;
  endpointKey: OfflineEndpointKey;
  technicianId: number;
}

function serveCachedOrFallback(params: ServeCachedParams) {
  const { storage, toastService, req, cacheKey, clientId, endpointKey, technicianId } = params;

  return from(resolveCachedResponse(storage, cacheKey, clientId, endpointKey, technicianId)).pipe(
    switchMap((cached) => {
      if (cached) {
        return of(new HttpResponse({ body: cached.body, status: cached.status }));
      }

      toastService.show(
        $localize`:@@offlineNoCache:This data is not available offline.`,
        TOAST_TYPE.ERROR,
      );
      return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'Offline', url: req.url }));
    }),
  );
}

async function resolveCachedResponse(
  storage: OfflineStorageService,
  cacheKey: string,
  clientId: string,
  endpointKey: OfflineEndpointKey,
  technicianId: number,
): Promise<{ body: unknown; status: number } | undefined> {
  const cached = await storage.getHttpCache(cacheKey);
  if (cached) {
    return cached;
  }

  if (technicianId && TECHNICIAN_SEARCH_ENDPOINTS.includes(endpointKey)) {
    const workspaceDefault = await resolveWorkspaceDefaultCache(storage, clientId, technicianId, endpointKey);
    if (workspaceDefault) {
      return workspaceDefault;
    }

    const legacyFallback = await storage.findHttpCacheForTechnician(clientId, endpointKey, technicianId);
    if (legacyFallback) {
      return legacyFallback;
    }
  }

  return undefined;
}

async function resolveWorkspaceDefaultCache(
  storage: OfflineStorageService,
  clientId: string,
  technicianId: number,
  endpointKey: OfflineEndpointKey,
): Promise<{ body: unknown; status: number } | undefined> {
  const workspace = await storage.getTechnicianWorkspace(clientId, technicianId);
  const field = WORKSPACE_DEFAULT_KEY_FIELDS[endpointKey];
  if (!workspace?.defaultCacheKeys || !field) {
    return undefined;
  }

  const defaultKey = workspace.defaultCacheKeys[field];
  if (!defaultKey) {
    return undefined;
  }

  return storage.getHttpCache(defaultKey);
}

async function isGuardedFormCacheWrite(
  endpointKey: string,
  requestParams: Record<string, unknown>,
  formState: OfflineFormStateService,
  clientId: string,
): Promise<boolean> {
  const serviceRequestId = Number(requestParams['serviceRequestId'] ?? 0);
  if (!serviceRequestId || !clientId) {
    return false;
  }

  if (endpointKey === 'SERVICE_REQUEST_FORM_OFFLINE_CONTEXT') {
    return formState.hasUnsyncedLocalForRequest(clientId, serviceRequestId, {
      formPlacementId: requestParams['formPlacementId'],
    });
  }

  if (endpointKey === 'SERVICE_REQUEST_FORM_PLACEMENT') {
    return formState.hasUnsyncedLocalForRequest(clientId, serviceRequestId, {
      formPlacementId: requestParams['placementId'],
    });
  }

  return false;
}
